import React from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import {
  DogPage,
  DogFactsBar,
  DogProfileActions
} from "components/Dogs/DogComponents.js";
import SEO from "components/SEO/SEO.js";
import DogGallery from "./DogGallery";

const agilityIcon = require("../../assets/img/icon-agility.png");
const barnHuntIcon = require("../../assets/img/icon-barnhunt.png");
const discIcon = require("../../assets/img/icon-disc.png");
const dockDivingIcon = require("../../assets/img/icon-dockdiving.png");
const flyballIcon = require("../../assets/img/icon-flyball.png");
const herdingIcon = require("../../assets/img/icon-herding.png");
const rallyIcon = require("../../assets/img/icon-rally.png");
const stackImage = require("../../assets/img/dogs/gambit/stack.jpg");
const pedigreeImage = require("../../assets/img/dogs/gambit/pedigree.jpg");
const clearances = [
  {
    id: "gambit-optimal-known-disorders-2020",
    label: "Optimal Selection DNA Report",
    shortLabel: "Optimal DNA",
    category: "Genetic",
    detailLabel: "Known Disorders",
    date: "January 2020",
    image: require("../../assets/img/dogs/gambit/clearance1.jpg"),
    alt: "Gambit Optimal Selection DNA report from January 2020"
  },
  {
    id: "gambit-optimal-additional-tests-2020",
    label: "Optimal Selection Additional Tests",
    shortLabel: "Additional DNA",
    category: "Genetic",
    detailLabel: "Additional DNA Tests",
    date: "January 2020",
    image: require("../../assets/img/dogs/gambit/clearance2.jpg"),
    alt: "Gambit Optimal Selection additional DNA tests from January 2020"
  },
  {
    id: "gambit-embark-dna-2018",
    label: "Embark DNA Test Report",
    shortLabel: "Embark DNA",
    category: "Genetic",
    detailLabel: "DNA Panel",
    date: "September 2018",
    image: require("../../assets/img/dogs/gambit/clearance3.jpg"),
    alt: "Gambit Embark DNA test report from September 2018"
  }
];

const galleryImages = [
  require("../../assets/img/dogs/gambit/reg1.jpg"),
  require("../../assets/img/dogs/gambit/reg2.jpg"),
  require("../../assets/img/dogs/gambit/reg3.jpg"),
  require("../../assets/img/dogs/gambit/reg4.jpg"),
  require("../../assets/img/dogs/gambit/reg5.jpg"),
  require("../../assets/img/dogs/gambit/reg6.jpg"),
  require("../../assets/img/dogs/gambit/stack2.jpg"),
  require("../../assets/img/dogs/gambit/agility1.jpg"),
  require("../../assets/img/dogs/gambit/agility2.jpg"),
  require("../../assets/img/dogs/gambit/agility4.jpg"),
  require("../../assets/img/dogs/gambit/agility3.jpg"),
  require("../../assets/img/dogs/gambit/agility5.jpg"),
  require("../../assets/img/dogs/gambit/disc1.jpg"),
  require("../../assets/img/dogs/gambit/disc2.jpg"),
  require("../../assets/img/dogs/gambit/disc3.jpg"),
  require("../../assets/img/dogs/gambit/dock2.jpg"),
  require("../../assets/img/dogs/gambit/dock4.jpg"),
  require("../../assets/img/dogs/gambit/dock5.jpg")
];

const gambitProfile = {
  name: "Gambit",
  registeredName: "PNAC MACH ADCH-BRONZE EC's Cajun Charm in the Thieves Guild MXB MXJB CGC RA NF DM DDGE DDP",
  about: "Gambit is a black tri border collie bred by Elisa Chavez in California and raised and trained by me. He excelled at multiple sports and is a dream to work with. Gambit unfortunately produced an epileptic puppy in his only litter in another kennel and since his littermate also produced an epileptic puppy and he developed a cataract, he is no longer part of the breeding program.",
  facts: [
    { label: "Breed", value: "Border Collie" },
    { label: "Born", value: "12/2/15" },
    { label: "Height", value: '22"' },
    { label: "Weight", value: "48lbs" }
  ],
  temperament: "Gambit is known to be goofy and loving. He happy squeaks for people and dogs he knows and will wiggle up to people for attention. He is the clingy, momma's boy of the house who follows me around. He does well in busy environments and performs and relaxes in them with ease. He gets along well with the majority of dogs but can be insecure with some large males after some bad experiences. He is a great puppy raiser and knows how to make shy dogs feel comfortable around him.",
  athleticAbility: "Gambit is the perfect dog for any level of handler. He makes an effort to get behaviors right and will slow down to think, then speed right back up once told he's correct. He is very thoughtful with his actions and body. He never went through an awkward phase and has always been very athletic, already jumping 22 feet off a dock at 10 months old.",
  video: {
    src: "https://www.youtube.com/embed/GSCUcTibfR4"
  }
};

const healthGroups = [
  {
    title: "Orthopedic & Screening",
    items: [
      { label: "Hips", value: "OFA Good" },
      { label: "Elbows", value: "Normal*" },
      { label: "Shoulders", value: "Normal*" },
      { label: "Eyes", value: "Passing w/ cataract**" }
    ],
    note: "* Cleared by specialist. All x-rays available on request. ** Suspected not inherited."
  },
  {
    title: "DNA Panel",
    items: [
      { label: "CEA", value: "Clear" },
      { label: "DM", value: "Clear" },
      { label: "SN", value: "Clear" },
      { label: "TNS", value: "Clear" },
      { label: "IGS", value: "Carrier" },
      { label: "NCL5", value: "Clear" },
      { label: "G/G", value: "Clear" },
      { label: "DH", value: "Clear" },
      { label: "EOD", value: "Clear" }
    ]
  }
];

const competitionHighlights = [
  {
    sport: "Agility",
    icon: agilityIcon,
    groups: [
      {
        label: "World Teams",
        items: [
          "2020/2022 IFCS Win on",
          "2020 European Open Alternate",
          "2019 European Open Win on",
          "2018 European Open Pick"
        ]
      },
      {
        label: "National Podiums",
        items: [
          "2024 AKC National Championship 1st",
          "2024 UKI Invitational Games 3rd",
          "2023 UKI US Open Games 1st",
          "2023 UKI US Open Biathlon 1st",
          "2023 AKC National Championship 5th",
          "2022 UKI US Open Biathlon 1st",
          "2022 UKI US Open Masters Series 1st",
          "2022 UKI US Open National Final 1st",
          "2022 UKI US Open Games Challenge 1st",
          "2022 AKC National Championship 4th",
          "2021 UKI US Open Biathlon 1st",
          "2021 UKI US Open Masters Series 2nd",
          "2021 USDAA Cynosport Grand Prix 2nd",
          "2021 AKC National Championship 3rd",
          "2019 USDAA Cynosport Biathlon 1st",
          "2019 UKI US Open Biathlon 1st",
          "2018 USDAA Cynosport Team Standard & Jumpers 2nd"
        ]
      },
      {
        label: "Regional Podiums",
        items: [
          "2024 UKI Southeast Cup 1st",
          "2023 USDAA Southeast Biathlon 1st",
          "2023 USDAA Southeast Team 1st",
          "2023 USDAA Southeast Steeplechase 1st",
          "2020 UKI Southeast Cup Biathlon 3rd",
          "2019 UKI Classic Biathlon 1st",
          "2019 UKI Classic Pentathlon 2nd",
          "2019 UKI Classic Speedstakes 2nd",
          "2019 USDAA Southeast Biathlon 2nd",
          "2019 USDAA Southeast Team 3rd",
          "2019 USDAA Mid-Atlantic Biathlon 3rd",
          "2018 USDAA Pacific Northwest Steeplechase 1st",
          "2018 USDAA Pacific Northwest Biathlon 1st",
          "2018 USDAA Southwest Steeplechase 3rd",
          "2018 USDAA Wild West Biathlon 1st",
          "2018 USDAA Wild West Steeplechase 1st",
          "2017 USDAA Western Steeplechase 3rd"
        ]
      }
    ]
  },
  {
    sport: "Dock Diving",
    icon: dockDivingIcon,
    groups: [
      {
        label: "Personal Bests",
        items: ["Distance: 26ft 5in", "Air retrieve: 21ft"]
      },
      {
        label: "Ranking",
        items: ["#1 Border Collie in Air Retrieve 2019"]
      }
    ]
  },
  {
    sport: "Disc",
    icon: discIcon,
    groups: [
      {
        label: "Venues",
        items: ["UpDog", "Local Toss and Fetch"]
      }
    ]
  },
  {
    sport: "Flyball",
    icon: flyballIcon,
    groups: [
      {
        label: "Personal Best",
        items: ["PB: 4.0"]
      }
    ]
  },
  {
    sport: "Rally",
    icon: rallyIcon,
    groups: [
      {
        label: "Title",
        items: ["Novice title achieved at 7 months"]
      }
    ]
  },
  {
    sport: "Barn Hunt",
    icon: barnHuntIcon,
    groups: [
      {
        label: "Title",
        items: ["Novice title achieved at 10 months"]
      }
    ]
  },
  {
    sport: "Herding",
    icon: herdingIcon,
    groups: [
      {
        label: "Training",
        items: ["Basic training with strong instinct"]
      }
    ]
  }
];

function DogSectionHeading({ eyebrow, title, children }) {
  return (
    <div className="dog-detail-section-heading">
      {eyebrow && <p>{eyebrow}</p>}
      <h2>{title}</h2>
      <span aria-hidden="true"></span>
      {children}
    </div>
  );
}

function GambitPage() {
  const hasVideo = Boolean(gambitProfile.video && gambitProfile.video.src);

  return (
    <>
      <SEO
        title="Gambit | Border Collie Sport Dog"
        description="Meet Gambit, a health-tested black tri border collie from Endgame Sport Dogs who made multiple world teams, placed at national agility events, and is retired from breeding."
        path="/dogs/gambit"
      />
      <DogPage
        headerBg="gambit"
        pageClassName="dog-detail-page dog-gambit-page"
      >
        <section className="dog-facts-wrap" aria-label="Gambit at a glance">
          <Container>
            <DogFactsBar facts={gambitProfile.facts} />
          </Container>
        </section>

        <section className="dog-detail-section dog-about-section">
          <Container>
            <div className="dog-about-layout">
              <div className="dog-about-copy">
                <div className="dog-back-link">
                  <Link to="/dogs">
                    <ArrowLeft aria-hidden="true" size={14} strokeWidth={2.2} /> Back to Dogs
                  </Link>
                </div>
                <DogSectionHeading eyebrow={gambitProfile.registeredName}title={`About ${gambitProfile.name}`} />
                <p>{gambitProfile.about}</p>
              </div>
              <figure className="dog-stack-figure">
                <img alt="Gambit stacked in profile" src={stackImage} />
                <figcaption>{gambitProfile.stats}</figcaption>
              </figure>
            </div>

            <div className="dog-health-spec-panel site-bg-light-blue">
              <div className="dog-health-panel-heading">
                <h2>Health & Genetics</h2>
                <span aria-hidden="true"></span>
              </div>
              <div className="dog-health-results">
                {healthGroups.map(group => (
                  <section
                    className={`dog-health-spec-group${group.title === "DNA Panel" ? " dog-health-dna-group" : ""}`}
                    key={group.title}
                  >
                    <h3>{group.title}</h3>
                    <dl>
                      {group.items.map(item => (
                        <div className="dog-health-result" key={`${group.title}-${item.label}`}>
                          <span className="dog-health-check" aria-hidden="true">✓</span>
                          <dt>{item.label}</dt>
                          <dd>{item.value}</dd>
                        </div>
                      ))}
                    </dl>
                    {group.note && <p>{group.note}</p>}
                  </section>
                ))}
              </div>
              <section className="dog-health-spec-group dog-health-actions-group">
                <h3>Documents</h3>
                <DogProfileActions
                  dogName={gambitProfile.name}
                  pedigreeImage={pedigreeImage}
                  pedigreeAlt="Gambit pedigree"
                  clearances={clearances}
                  className="dog-profile-actions"
                />
              </section>
            </div>

            <div className="dog-traits-layout">
              <article>
                <h2>Temperament</h2>
                <span aria-hidden="true"></span>
                <p>{gambitProfile.temperament}</p>
              </article>
              <article>
                <h2>Athletic Ability</h2>
                <span aria-hidden="true"></span>
                <p>{gambitProfile.athleticAbility}</p>
              </article>
            </div>
          </Container>
        </section>

        <div className="dog-performance-wrap">
          {hasVideo && (
            <section className="dog-detail-section dog-video-section">
              <Container>
                <DogSectionHeading eyebrow={`${gambitProfile.name} in Action`} title="Agility highlights" />
                <div className="dog-video-feature">
                  <div className="dog-video-embed">
                    <h3>{gambitProfile.video.title}</h3>
                    <div className="video-frame">
                      <iframe
                        src={gambitProfile.video.src}
                        title={gambitProfile.video.title}
                        loading="lazy"
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                  <div className="dog-video-caption">
                    <p>Gambit is in the masters/championship level in AKC, USDAA, and UKI and jumps 24 inches regular. He has a long stride and is careful not to knock bars. He excels at commitment and working at a distance. He is an amazing partner and made his first world team on the weekend of his second birthday.</p>
                  </div>
                </div>
              </Container>
            </section>
          )}

          <section className="dog-detail-section dog-competition-section">
            <Container>
              <div className="dog-health-panel-heading dog-competition-panel-heading">
                <h2>Competition Highlights</h2>
                <span aria-hidden="true"></span>
              </div>
              <div className="dog-competition-grid dog-competition-grid-featured">
                {competitionHighlights.map(group => (
                  <section className="dog-sport-highlight" key={group.sport}>
                    <h3>
                      <span className="dog-sport-highlight-icon" aria-hidden="true">
                        <img alt="" src={group.icon} />
                      </span>
                      {group.sport}
                    </h3>
                    {group.groups.map(section => (
                      <div className="dog-sport-highlight-group" key={`${group.sport}-${section.label}`}>
                        <h4>{section.label}</h4>
                        <ul>
                          {section.items.map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </section>
                ))}
              </div>
            </Container>
          </section>
        </div>

        <section className="dog-detail-section dog-gallery-section site-bg-dark-navy">
          <Container>
            <DogSectionHeading title="Photo Gallery" />
          </Container>
          <DogGallery images={galleryImages} />
        </section>
      </DogPage>
    </>
  );
}

export default GambitPage;
